import { useMemo } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { catalogApi } from '../api/endpoints/catalog';
import { platformApi } from '../api/endpoints/platform';
import { buildLabelResolver, type LabelResolver } from './useLabelResolver';
import { useResolvedTenantLevels } from './useResolvedTenantLevels';

/** Catálogo de permisos del tenant que ve un actor de plataforma. */
export function tenantCatalogQueryOptions(tenantId: string) {
    return queryOptions({
        queryKey: ['catalog', 'permissions', tenantId],
        queryFn: () => catalogApi.listPermissions(tenantId),
        staleTime: 5 * 60_000,
        enabled: !!tenantId,
    });
}

/** Resolvedor de etiquetas con el catálogo, los niveles y el nombre del tenant visto (no el del actor). */
export function useTenantLabelResolver(tenantId: string): LabelResolver {
    const catalogQ = useQuery(tenantCatalogQueryOptions(tenantId));
    const tenantQ = useQuery({
        queryKey: ['platform', 'tenants', tenantId, 'detail'],
        queryFn: () => platformApi.getTenant(tenantId),
        enabled: !!tenantId,
    });

    const permissions = catalogQ.data?.items;
    const tenant = tenantQ.data;
    // Mismo fallback que TenantShell: si `tenant.levels` llega vacío, cae a los niveles de su app.
    const resolvedLevels = useResolvedTenantLevels(tenant);

    return useMemo(
        () =>
            buildLabelResolver({
                permissions,
                levels: resolvedLevels,
                tenantId,
                tenantDisplayName: tenant?.displayName,
            }),
        [permissions, resolvedLevels, tenant?.displayName, tenantId],
    );
}
